import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  acceptResourceAllocationSettlement,
  getResourceAllocationFilterEmployees,
  getResourceAllocationSummary,
  rejectResourceAllocationSettlement,
} from "../services/resourceAllocation.service";
import { monthNames } from "../utils/consts";
import { isResourceAllocationReadOnly } from "../utils/resourceAllocationView";
import UserContext from "../modules/AppRouter/UserContext";
import type {
  IResourceAllocationEmployeeOptionApi,
  IResourceAllocationSummaryRowApi,
} from "../services/types/resourceAllocation";

type LoadDataOptions = {
  skipEmployees?: boolean;
};

type UseResourceAllocationSummaryOptions = {
  onEmployeeIdInvalid?: () => void;
};

export default function useResourceAllocationSummary(
  selectedMonth: string,
  selectedYear: number,
  selectedEmployeeId: string,
  options?: UseResourceAllocationSummaryOptions
) {
  const { userData } = useContext(UserContext);
  const workContextRole = userData?.data?.baseRole;
  const onEmployeeIdInvalid = options?.onEmployeeIdInvalid;

  const [rows, setRows] = useState<IResourceAllocationSummaryRowApi[]>([]);
  const [filterEmployees, setFilterEmployees] = useState<IResourceAllocationEmployeeOptionApi[]>([]);
  const [year, setYear] = useState(selectedYear);
  const [month, setMonth] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [readOnlyFromApi, setReadOnlyFromApi] = useState(false);

  const loadGenerationRef = useRef(0);
  const filterEmployeesRef = useRef(filterEmployees);
  filterEmployeesRef.current = filterEmployees;

  const monthIndex = monthNames.indexOf(selectedMonth);
  const readOnlyMode = isResourceAllocationReadOnly(readOnlyFromApi, workContextRole);

  const loadData = useCallback(async (loadOptions?: LoadDataOptions) => {
    if (monthIndex < 0) return;

    const generation = ++loadGenerationRef.current;
    setIsLoading(true);

    try {
      let employees = filterEmployeesRef.current;

      if (!loadOptions?.skipEmployees) {
        employees = await getResourceAllocationFilterEmployees();
        if (loadGenerationRef.current !== generation) return;
        setFilterEmployees(employees);
      }

      const employeeIdForQuery =
        selectedEmployeeId
        && employees.some((employee) => employee.employeeId === selectedEmployeeId)
          ? selectedEmployeeId
          : undefined;

      if (selectedEmployeeId && !employeeIdForQuery) {
        onEmployeeIdInvalid?.();
      }

      const data = await getResourceAllocationSummary(
        selectedYear,
        monthIndex + 1,
        employeeIdForQuery
      );
      if (loadGenerationRef.current !== generation) return;

      setRows(data.rows);
      setYear(data.year);
      setMonth(data.month);
      setReadOnlyFromApi(data.readOnlyMode);
    } catch (err) {
      if (loadGenerationRef.current !== generation) return;
      console.error("❌ Błąd pobierania alokacji zasobów:", err);
    } finally {
      if (loadGenerationRef.current === generation) {
        setIsLoading(false);
      }
    }
  }, [monthIndex, onEmployeeIdInvalid, selectedEmployeeId, selectedYear, workContextRole]);

  const acceptSettlement = async (row: IResourceAllocationSummaryRowApi) => {
    if (monthIndex < 0 || readOnlyMode) return;

    await acceptResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1
    );

    await loadData({ skipEmployees: true });
  };

  const rejectSettlement = async (
    row: IResourceAllocationSummaryRowApi,
    rejectionReason: string
  ) => {
    if (monthIndex < 0 || readOnlyMode) return;

    await rejectResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1,
      rejectionReason
    );

    await loadData({ skipEmployees: true });
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    rows,
    filterEmployees,
    year,
    month,
    isLoading,
    readOnlyMode,
    refetch: () => loadData({ skipEmployees: true }),
    acceptSettlement: readOnlyMode ? undefined : acceptSettlement,
    rejectSettlement: readOnlyMode ? undefined : rejectSettlement,
    monthIndex,
  };
}
